import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import selectUser from '../../../store/selectors/appSelector';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import { ROUTE_CONSTANTS } from '../../../views/app/RouteBuilding/routeConstants';
import { JOB_ORDER_STATUSES } from '../../../views/jobOrders/jobOrderConstants';
import EmployeeReserveListCard from '../../EmployeeReserveListCard';

import EmployeeRosterCard from './SubComponents/EmployeeRosterCard';

const useStyles = makeStyles({
  content: {
    padding: '12px',
    fontFamily: PRIMARY_FONT[300],
    overflow: 'auto',
  },
  titleText: {
    width: '100%',
    display: 'block',
    textAlign: 'left',
    fontSize: '20px',
    fontFamily: PRIMARY_FONT[700],
    lineHeight: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  corpNameText: {
    maxWidth: '170px',
    display: 'block',
    textAlign: 'left',
    fontSize: '14px',
    fontFamily: PRIMARY_FONT[300],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: '3px',
  },
  exIdText: {
    display: 'block',
    textAlign: 'left',
    fontSize: '14px',
    fontFamily: PRIMARY_FONT[300],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  employees: {
    display: 'flex',
    flexDirection: 'column',
    '& .title': {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '10px',
      lineHeight: '12px',
      fontFamily: PRIMARY_FONT[300],
      paddingTop: '12px',
      textTransform: 'uppercase',
      '& .listHeader': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
    },
    '& .list': {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    '& .reserveListTitle': {
      display: 'flex',
      justifyContent: 'flex-end',
      fontSize: '10px',
      lineHeight: '12px',
      fontWeight: 300,
      textTransform: 'uppercase',
      '& .listHeader': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
    },
  },
  infoIcon: { fontSize: '14px' },

  reserveListText: {
    fontFamily: PRIMARY_FONT[700],
    textAlign: 'left',
    fontSize: '12px',
    lineHeight: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  detailButton: {
    backgroundColor: 'transparent',
    cursor: 'pointer',
    borderRadius: '5px',
    width: '170px',
    color: '#7A23FF',
    border: '1px solid #7A23FF',
    height: '25px',
    padding: '4px 12px 4px 12px',
    gap: '4px',
    opacity: '0px',
    marginTop: 8,
  },
});

const UpcomingRosterView = ({ jobOrder, isExpanded }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const placements =
    jobOrder?.placements?.filter((p) => p?.state?.status === JOB_ORDER_STATUSES.ACTIVE) || [];
  const numOpenings = jobOrder?.numOpenings || 0;
  const sortedPlacements = placements?.sort(
    (a, b) => new Date(a?.metadata?.created) - new Date(b?.metadata?.created)
  );
  const activePlacements = sortedPlacements.slice(0, numOpenings);
  const reserveList = sortedPlacements.slice(numOpenings);

  return (
    <Box>
      <Box
        className={classes.content}
        sx={{
          height: isExpanded ? '740px' : '324px',
        }}
      >
        <div className={classes.employees}>
          <Typography className={classes.titleText}> {jobOrder?.name} </Typography>
          {user.role === UserRole.ADMIN && (
            <Box sx={{ display: 'flex' }}>
              <Typography className={classes.corpNameText}>
                {jobOrder?.corporation?.name}
              </Typography>
              <Typography className={classes.exIdText}>
                {`• EXID  ${jobOrder?.metadata?.bullhorn?.id}`}
              </Typography>
            </Box>
          )}
          <button
            type="button"
            className={classes.detailButton}
            onClick={() => {
              const jobIdPath = generatePath(`/${ROUTE_CONSTANTS.JOB_ORDERS}/:id`, {
                id: jobOrder?._id,
              });
              navigate(`${jobIdPath}${location.search}`);
            }}
          >
            {LanguageConverter('jobOrder.manageJobAndRoster')}
          </button>
          <div className="title">
            <div>
              {LanguageConverter('jobOrder.employees')}({activePlacements?.length})
            </div>
          </div>
          <div className="list">
            {activePlacements.map((placement) => (
              <EmployeeRosterCard key={placement._id} placement={placement} />
            ))}
          </div>

          {reserveList.length > 0 && (
            <>
              <Divider className={classes.reserveListText}>
                {LanguageConverter('roster.reserveList')}
              </Divider>

              <div className="reserveListTitle">
                <div className="reserveListTitle listHeader">
                  <HelpOutlineIcon className={classes.infoIcon} />
                  {LanguageConverter('jobOrder.roster.addToRoster')}
                </div>
              </div>
              <div className="list">
                {reserveList.map((placement) => (
                  <EmployeeReserveListCard
                    key={placement._id}
                    profilePic={placement?.profilePic}
                    fullName={placement?.candidateName}
                    checkInStatus={placement?.checkInStatus}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </Box>
    </Box>
  );
};

UpcomingRosterView.propTypes = {
  isExpanded: PropTypes.bool,
  jobOrder: PropTypes.shape({
    _id: PropTypes.string,
    corporation: PropTypes.shape({
      name: PropTypes.string,
    }),
    name: PropTypes.string,
    numOpenings: PropTypes.number,
    placements: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      })
    ),
    start: PropTypes.number,
    end: PropTypes.number,
    metadata: PropTypes.shape({
      bullhorn: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
  }),
};

export default UpcomingRosterView;
